import { SyntheticEvent, useCallback, useEffect, useRef } from 'react';

type AutocompleteInputChangeReason = 'input' | 'reset' | 'clear';

type UseAutocompleteFocusProps = {
  onSearch?: (search: string) => void;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  options?: Array<unknown>;
  inputValue?: string;
  /**
   * Whether focusing the input should automatically open the dropdown.
   * Defaults to true.
   */
  autoOpenOnFocus?: boolean;
};

type UseAutocompleteFocusReturn = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  rootRef: React.RefObject<HTMLDivElement | null>;
  handleInputChange: (
    event: SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  handleFocus: () => void;
  handleBlur: (event: React.FocusEvent) => void;
  handleOpenInternal: () => void;
  handleRootClick: (event: SyntheticEvent) => void;
};

// keep focus on input when options are fetched
export const useAutocompleteFocus = ({
  onSearch,
  open,
  onOpen,
  onClose,
  options,
  inputValue,
  autoOpenOnFocus = true,
}: UseAutocompleteFocusProps): UseAutocompleteFocusReturn => {
  const searchRef = useRef<((value: string) => void) | undefined>(onSearch);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    searchRef.current = onSearch;
  }, [onSearch]);

  const handleRootClick = useCallback((event: SyntheticEvent) => {
    const target = event.target as HTMLElement;

    // Don't handle clicks on buttons, svgs, or if input is already focused
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'svg' ||
      target.closest('button') ||
      target.closest('svg') ||
      document.activeElement === inputRef.current
    ) {
      return;
    }

    // Only focus the input if clicking on the input container area
    // Check if the click is within the input container (identified by data attribute)
    // This prevents opening the dropdown when clicking on empty space in the tags area
    const inputContainer = target.closest('[data-autocomplete-input-container]');

    if (inputContainer && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleOpenInternal = useCallback(() => {
    if (inputRef.current && !inputRef.current.value && searchRef.current) {
      searchRef.current('');
    }
    onOpen?.();
  }, [onOpen]);

  const handleInputChange = useCallback(
    (event: SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) => {
      const wasFocused = document.activeElement === inputRef.current;

      if (reason === 'input') {
        // User is typing: propagate search value
        searchRef.current?.(value);
      } else if (reason === 'reset' || reason === 'clear') {
        // Selection or manual clear: reset search text
        searchRef.current?.('');
      }

      if (wasFocused && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }
    },
    [],
  );

  useEffect(() => {
    if (open && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [options, open, inputValue]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    if (!open && autoOpenOnFocus) {
      handleOpenInternal();
    } else if (inputRef.current && !inputRef.current.value && searchRef.current) {
      searchRef.current('');
    }
  }, [open, autoOpenOnFocus, handleOpenInternal]);

  const handleBlur = useCallback(
    (_event: React.FocusEvent) => {
      blurTimeoutRef.current = setTimeout(() => {
        const isWithinComponent =
          rootRef.current?.contains(document.activeElement) || document.activeElement === rootRef.current;

        // Only refocus if the user is still within the component AND the dropdown is open
        // This prevents the dropdown from staying open when clicking outside
        if (isWithinComponent && inputRef.current && open) {
          inputRef.current.focus();
        }
      }, 50);
    },
    [open],
  );

  // Add click-outside listener to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (open && rootRef.current) {
        const target = event.target as Element;

        // Check if the click is within the autocomplete component or its dropdown
        const isWithinAutocomplete = rootRef.current.contains(target);
        const isDropdownOption =
          target.closest('[role="option"]') ||
          target.closest('[role="listbox"]') ||
          target.closest('[data-baseui-autocomplete-list]') ||
          target.closest('[data-baseui-autocomplete-item]');

        // Only close if the click is outside both the component and the dropdown
        if (!isWithinAutocomplete && !isDropdownOption) {
          onClose?.();
        }
      }
    };

    if (open) {
      // Use click instead of mousedown to allow option selection to complete first
      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [open, onClose]);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    [],
  );

  return {
    inputRef,
    rootRef,
    handleInputChange,
    handleFocus,
    handleBlur,
    handleOpenInternal,
    handleRootClick,
  };
};
